// src/components/CaseDetailsPage.js
// Full-page "War Room" dashboard for case management

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'animate.css';
import { formatCurrency } from '../utils/currency';
import LinkedEmails from './LinkedEmails';
import ComposeEmail from './ComposeEmail';
import ConveyancingDetailPanel from './ConveyancingDetailPanel';
import DocumentExplorer from './DocumentExplorer';
import TrustRequisitionPanel from './TrustRequisitionPanel';
import TrustLedgerPanel from './TrustLedgerPanel';
import NewTaskModal from './shared/NewTaskModal';
import LogCommunicationModal from './shared/LogCommunicationModal';
import LogTimeEntryModal from './shared/LogTimeEntryModal';
import LogDisbursementModal from './shared/LogDisbursementModal';
import CaseEditModal from './shared/CaseEditModal';

const CaseDetailsPage = ({
  authAxios,
  clients,
  showFormMessage,
  caseId: caseIdProp,
  initialTab: initialTabProp,
  // Module Access Control: when the firm has turned the invoicing module off
  // for this junior role, the in-case Billing sub-tab is hidden here too (the
  // API 403s /invoices/ regardless). Absent/true prop => shown, as before.
  moduleAccess = null,
}) => {
  const billingBlocked = moduleAccess?.invoicing === false;
  // This page is rendered two ways in this app: as the element of a real
  // react-router <Route path="/cases/:id/:tab?"> (useParams populated), or
  // directly as a JSX child with caseId/initialTab passed as plain props
  // (useParams returns {} in that case, since there's no matching Route).
  // Support both so the page works regardless of which path rendered it.
  const params = useParams();
  const caseId = caseIdProp || params.id;
  const initialTab = initialTabProp || params.tab;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  // If a deep link lands on the Billing sub-tab but the firm has turned
  // invoicing off for this role, fall back to Overview.
  useEffect(() => {
    if (billingBlocked && activeTab === 'billing') setActiveTab('overview');
  }, [billingBlocked, activeTab]);
  const [showCompose, setShowCompose] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState({
    documents: [],
    timeEntries: [],
    expenseEntries: [],
    invoices: [],
    tasks: [],
    communicationLogs: [],
    events: []
  });
  const [users, setUsers] = useState([]);
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showLogCommModal, setShowLogCommModal] = useState(false);
  const [showLogTimeModal, setShowLogTimeModal] = useState(false);
  const [showLogExpenseModal, setShowLogExpenseModal] = useState(false);

  // Firm's staff users, for the New Task modal's "Assigned To" picker.
  useEffect(() => {
    if (!authAxios) return;
    let cancelled = false;
    authAxios.get('/users/')
      .then(resp => { if (!cancelled) setUsers(resp.data?.results || resp.data || []); })
      .catch(() => { if (!cancelled) setUsers([]); });
    return () => { cancelled = true; };
  }, [authAxios]);
  // Stable identity (not a fresh closure every render) so DocumentExplorer's
  // useCallback/useEffect dependencies don't churn and re-fetch needlessly.
  const handleDocumentExplorerToast = useCallback((msg, type) => {
    showFormMessage?.(type, msg);
  }, [showFormMessage]);

  // Fetch case details
  const fetchCaseDetails = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const resp = await authAxios.get(`/cases/${caseId}/`);
      setCaseData(resp.data);
    } catch (err) {
      console.error('Failed to fetch case details', err);
      showFormMessage?.('error', 'Failed to load case details');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, showFormMessage]);

  // Fetch all related records
  const fetchRecords = useCallback(async () => {
    if (!caseId) return;
    try {
      const [docs, time, expense, inv, tasks, comms, events] = await Promise.all([
        authAxios.get('/documents/', { params: { case: caseId } }),
        authAxios.get('/time-entries/', { params: { case: caseId } }),
        authAxios.get('/expense-entries/', { params: { case: caseId } }),
        authAxios.get('/invoices/', { params: { case: caseId } }),
        authAxios.get('/tasks/', { params: { case: caseId } }),
        authAxios.get('/communication-logs/', { params: { case: caseId } }),
        authAxios.get('/events/', { params: { case: caseId } }).catch(() => ({ data: { results: [] } }))
      ]);

      setRecords({
        documents: docs.data?.results || docs.data || [],
        timeEntries: time.data?.results || time.data || [],
        expenseEntries: expense.data?.results || expense.data || [],
        invoices: inv.data?.results || inv.data || [],
        tasks: tasks.data?.results || tasks.data || [],
        communicationLogs: comms.data?.results || comms.data || [],
        events: events.data?.results || events.data || []
      });
    } catch (err) {
      console.error('Failed to fetch case records', err);
    }
  }, [authAxios, caseId]);

  useEffect(() => {
    fetchCaseDetails();
    fetchRecords();
  }, [fetchCaseDetails, fetchRecords, caseId]);

  // Calculate case statistics
  const stats = useMemo(() => {
    if (!caseData) return {};

    const totalHoursBilled = records.timeEntries
      .filter(t => t.is_billable && t.invoice)
      .reduce((sum, t) => sum + (parseFloat(t.duration_hours) || 0), 0);

    const totalHoursUnbilled = records.timeEntries
      .filter(t => t.is_billable && !t.invoice)
      .reduce((sum, t) => sum + (parseFloat(t.duration_hours) || 0), 0);

    const totalRevenue = records.invoices
      .filter(inv => ['Sent', 'Paid', 'Overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);

    const totalExpenses = records.expenseEntries
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

    const openTasks = records.tasks.filter(t => t.status !== 'Completed').length;
    const completedTasks = records.tasks.filter(t => t.status === 'Completed').length;

    const nextCourtDate = records.events
      .filter(e => e.event_type === 'Court Hearing' && new Date(e.start_time) > new Date())
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];

    return {
      totalHoursBilled,
      totalHoursUnbilled,
      totalRevenue,
      totalExpenses,
      openTasks,
      completedTasks,
      nextCourtDate
    };
  }, [caseData, records]);

  // Activity timeline - combine all activities
  const activityTimeline = useMemo(() => {
    const activities = [];

    records.documents.forEach(doc => {
      activities.push({
        type: 'document',
        icon: 'bi-file-earmark',
        color: 'blue',
        title: `Document uploaded: ${doc.title}`,
        timestamp: new Date(doc.uploaded_at),
        data: doc
      });
    });

    records.timeEntries.forEach(te => {
      activities.push({
        type: 'time',
        icon: 'bi-clock',
        color: 'green',
        title: `${te.duration_hours}h logged by ${te.user_name || 'Unknown'}`,
        description: te.description,
        timestamp: new Date(te.date),
        data: te
      });
    });

    records.tasks.forEach(task => {
      activities.push({
        type: 'task',
        icon: task.status === 'Completed' ? 'bi-check-circle' : 'bi-circle',
        color: task.status === 'Completed' ? 'green' : 'yellow',
        title: `Task: ${task.title}`,
        description: `Status: ${task.status}`,
        timestamp: new Date(task.created_at),
        data: task
      });
    });

    records.communicationLogs.forEach(comm => {
      activities.push({
        type: 'communication',
        icon: 'bi-chat-dots',
        color: 'purple',
        title: `${comm.communication_type || 'Communication'} with ${comm.contact_name || 'Unknown'}`,
        description: comm.summary,
        timestamp: new Date(comm.date),
        data: comm
      });
    });

    records.invoices.forEach(inv => {
      activities.push({
        type: 'invoice',
        icon: 'bi-receipt',
        color: 'indigo',
        title: `Invoice ${inv.invoice_number || inv.id} - ${inv.status}`,
        description: `Amount: ${formatCurrency(inv.total_amount)}`,
        timestamp: new Date(inv.issue_date),
        data: inv
      });
    });

    records.expenseEntries.forEach(exp => {
      activities.push({
        type: 'expense',
        icon: 'bi-cash-stack',
        color: 'red',
        title: `Disbursement logged: ${formatCurrency(exp.amount)} (${exp.category})`,
        description: exp.description,
        timestamp: new Date(exp.date),
        data: exp
      });
    });

    // Sort by timestamp desc
    return activities.sort((a, b) => b.timestamp - a.timestamp);
  }, [records]);

  const getClient = useCallback(() => {
    if (!caseData || !clients) return null;
    return clients.find(c => c.id === caseData.client);
  }, [caseData, clients]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <div className="mt-4 text-gray-600">Loading case details...</div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center text-gray-500">
          <i className="bi bi-exclamation-triangle text-6xl mb-4"></i>
          <div className="text-xl">Case not found</div>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const client = getClient();
  const clientName = client
    ? client.client_type === 'individual'
      ? `${client.first_name} ${client.last_name}`
      : client.organization_name
    : 'Unknown Client';

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        {/* Top bar */}
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-800"
            >
              <i className="bi bi-arrow-left text-xl"></i>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-800">{caseData.title}</h1>
              <div className="text-sm text-gray-600 mt-1">
                {clientName} • {caseData.case_type} • {caseData.status}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                caseData.status === 'Open'
                  ? 'bg-green-100 text-green-700'
                  : caseData.status === 'Closed'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              {caseData.status}
            </span>
            <button onClick={() => setShowEditCaseModal(true)} className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              <i className="bi bi-pencil mr-2"></i>
              Edit Case
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-2.5 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-white p-2 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Billed Hours</div>
              <div className="text-lg font-bold text-gray-800">{stats.totalHoursBilled?.toFixed(1) || 0}</div>
            </div>
            <div className="bg-white p-2 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Unbilled Hours</div>
              <div className="text-lg font-bold text-yellow-600">{stats.totalHoursUnbilled?.toFixed(1) || 0}</div>
            </div>
            <div className="bg-white p-2 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Revenue</div>
              <div className="text-lg font-bold text-green-600">{formatCurrency(stats.totalRevenue || 0)}</div>
            </div>
            <div className="bg-white p-2 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Expenses</div>
              <div className="text-lg font-bold text-red-600">{formatCurrency(stats.totalExpenses || 0)}</div>
            </div>
            <div className="bg-white p-2 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Tasks</div>
              <div className="text-lg font-bold text-gray-800">
                {stats.openTasks || 0} / {(stats.openTasks || 0) + (stats.completedTasks || 0)}
              </div>
            </div>
            <div className="bg-white p-2 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Next Court Date</div>
              <div className="text-sm font-bold text-blue-600">
                {stats.nextCourtDate ? new Date(stats.nextCourtDate.start_time).toLocaleDateString() : 'None'}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex gap-1 border-b border-gray-200 overflow-x-auto scrollbar-hide">
            {[
              { id: 'overview', label: 'Overview', icon: 'bi-grid' },
              { id: 'tasks', label: 'Tasks', icon: 'bi-check-circle', badge: stats.openTasks },
              { id: 'documents', label: 'Documents', icon: 'bi-folder', badge: records.documents.length },
              ...(billingBlocked ? [] : [{ id: 'billing', label: 'Billing', icon: 'bi-receipt', badge: records.invoices.length }]),
              { id: 'time_expenses', label: 'Time & Expenses', icon: 'bi-stopwatch', badge: records.timeEntries.length + records.expenseEntries.length },
              { id: 'emails', label: 'Emails', icon: 'bi-envelope' },
              { id: 'communications', label: 'Communications', icon: 'bi-chat', badge: records.communicationLogs.length },
              { id: 'timeline', label: 'Timeline', icon: 'bi-clock-history' },
              { id: 'audit-log', label: 'Audit Log', icon: 'bi-journal-text' },
              ...(caseData?.case_type === 'Conveyancing' ? [{ id: 'conveyancing', label: 'Conveyancing', icon: 'bi-house' }] : []),
              { id: 'trust_requisitions', label: 'Trust Requisitions', icon: 'bi-bank' },
              { id: 'trust_ledger', label: 'Trust Ledger', icon: 'bi-journal-check' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(`/cases/${caseId}/${tab.id}`);
                }}
                className={`px-3 py-2 text-sm font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Case Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-base font-semibold mb-3">Case Information</h3>
              <div className="space-y-2">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Client</div>
                  <div className="font-medium">{clientName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Case Type</div>
                  <div className="font-medium">{caseData.case_type}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Stage</div>
                  <div className="font-medium">{caseData.case_stage}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Opened</div>
                  <div className="font-medium">{new Date(caseData.date_opened).toLocaleDateString()}</div>
                </div>
                {caseData.description && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Description</div>
                    <div className="text-sm text-gray-700">{caseData.description}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Team */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-base font-semibold mb-3">Team</h3>
              <div className="space-y-2">
                {caseData.partner_name && (
                  <div className="flex items-center gap-2">
                    <i className="bi bi-person-badge text-blue-600"></i>
                    <span className="font-medium">Partner:</span>
                    <span>{caseData.partner_name}</span>
                  </div>
                )}
                {caseData.associate_names && caseData.associate_names.length > 0 && (
                  <div className="flex items-center gap-2">
                    <i className="bi bi-people text-green-600"></i>
                    <span className="font-medium">Associates:</span>
                    <span>{caseData.associate_names.join(', ')}</span>
                  </div>
                )}
                {caseData.paralegal_names && caseData.paralegal_names.length > 0 && (
                  <div className="flex items-center gap-2">
                    <i className="bi bi-person text-purple-600"></i>
                    <span className="font-medium">Paralegals:</span>
                    <span>{caseData.paralegal_names.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
              <h3 className="text-base font-semibold mb-3">Recent Activity</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {activityTimeline.slice(0, 10).map((activity, idx) => (
                  <div key={idx} className="flex gap-3 pb-3 border-b border-gray-100 last:border-0">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-${activity.color}-100 flex items-center justify-center`}>
                      <i className={`${activity.icon} text-${activity.color}-600`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{activity.title}</div>
                      {activity.description && (
                        <div className="text-sm text-gray-600 truncate">{activity.description}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {activity.timestamp.toLocaleDateString()} {activity.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Tasks</h3>
              <button onClick={() => setShowNewTaskModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                <i className="bi bi-plus-lg mr-2"></i>
                New Task
              </button>
            </div>
            <div className="space-y-2">
              {records.tasks.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No tasks yet</div>
              ) : (
                records.tasks.map(task => (
                  <div
                    key={task.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 mt-1">{task.description}</div>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}</span>
                          <span>Assigned: {task.assigned_to_name || 'Unassigned'}</span>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            task.status === 'Completed'
                              ? 'bg-green-100 text-green-700'
                              : task.status === 'In Progress'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {task.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full min-h-[600px]">
            <DocumentExplorer
              authAxios={authAxios}
              caseId={caseId}
              onToast={handleDocumentExplorerToast}
            />
          </div>
        )}

        {activeTab === 'billing' && !billingBlocked && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-base font-semibold mb-3">Billing & Invoices</h3>
            <div className="space-y-4">
              {/* Time Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-600">Billed Hours</div>
                  <div className="text-2xl font-bold text-blue-700">{stats.totalHoursBilled?.toFixed(1)}</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="text-sm text-gray-600">Unbilled Hours</div>
                  <div className="text-2xl font-bold text-yellow-700">{stats.totalHoursUnbilled?.toFixed(1)}</div>
                </div>
              </div>

              {/* Invoices */}
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Invoices</h4>
                {records.invoices.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No invoices yet</div>
                ) : (
                  <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Invoice #</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Amount</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.invoices.map(inv => (
                        <tr key={inv.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{inv.invoice_number || inv.id}</td>
                          <td className="px-4 py-2 text-sm">{new Date(inv.issue_date).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm font-medium">{formatCurrency(inv.total_amount)}</td>
                          <td className="px-4 py-2 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                inv.status === 'Paid'
                                  ? 'bg-green-100 text-green-700'
                                  : inv.status === 'Overdue'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'time_expenses' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Time & Expenses</h3>
              <div className="flex gap-2">
                <button onClick={() => setShowLogTimeModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                  <i className="bi bi-plus-lg mr-2"></i>Log Time Entry
                </button>
                <button onClick={() => setShowLogExpenseModal(true)} className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm">
                  <i className="bi bi-plus-lg mr-2"></i>Log Disbursement
                </button>
              </div>
            </div>

            <h4 className="font-semibold mb-2 text-sm text-gray-700">Time Entries</h4>
            {records.timeEntries.length === 0 ? (
              <div className="text-center text-gray-500 py-4 text-sm">No time entries yet</div>
            ) : (
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden mb-6">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Billable</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {records.timeEntries.map(te => (
                    <tr key={te.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">{te.description}</td>
                      <td className="px-4 py-2 text-sm">{parseFloat(te.duration_hours || te.hours || 0).toFixed(2)}h</td>
                      <td className="px-4 py-2 text-sm">{te.is_billable ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2 text-sm">{te.billing_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h4 className="font-semibold mb-2 text-sm text-gray-700">Disbursements</h4>
            {records.expenseEntries.length === 0 ? (
              <div className="text-center text-gray-500 py-4 text-sm">No disbursements yet</div>
            ) : (
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {records.expenseEntries.map(ex => (
                    <tr key={ex.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">{ex.description}</td>
                      <td className="px-4 py-2 text-sm">{ex.category}</td>
                      <td className="px-4 py-2 text-sm">{formatCurrency(ex.amount)}</td>
                      <td className="px-4 py-2 text-sm">{ex.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'emails' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Linked Emails</h3>
              <button
                onClick={() => setShowCompose(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                <i className="bi bi-pencil-square"></i> Compose Email
              </button>
            </div>
            <LinkedEmails
              authAxios={authAxios}
              caseId={caseId}
              caseNumber={caseData.case_number}
              clientEmail={caseData.client_email || ''}
              onToast={(msg, type) => showFormMessage?.(type, msg)}
            />
            {showCompose && (
              <ComposeEmail
                authAxios={authAxios}
                caseId={caseId}
                defaultTo={caseData?.client_email || ''}
                onClose={() => setShowCompose(false)}
                showFormMessage={showFormMessage}
              />
            )}
          </div>
        )}

        {activeTab === 'communications' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Communications</h3>
              <button onClick={() => setShowLogCommModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                <i className="bi bi-plus-lg mr-2"></i>
                Log Communication
              </button>
            </div>
            <div className="space-y-2">
              {records.communicationLogs.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No communications yet</div>
              ) : (
                records.communicationLogs.map(comm => (
                  <div key={comm.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium">{comm.communication_type || 'Communication'}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {comm.contact_name && `With: ${comm.contact_name}`}
                        </div>
                        {comm.summary && (
                          <div className="text-sm text-gray-700 mt-2">{comm.summary}</div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(comm.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-base font-semibold mb-3">Activity Timeline</h3>
            <div className="space-y-4">
              {activityTimeline.map((activity, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full bg-${activity.color}-100 flex items-center justify-center`}>
                      <i className={`${activity.icon} text-${activity.color}-600`}></i>
                    </div>
                    {idx < activityTimeline.length - 1 && (
                      <div className="w-0.5 flex-1 bg-gray-200 my-2"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="font-medium">{activity.title}</div>
                    {activity.description && (
                      <div className="text-sm text-gray-600 mt-1">{activity.description}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-2">
                      {activity.timestamp.toLocaleDateString()} at {activity.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'audit-log' && <AuditLogPanel authAxios={authAxios} caseId={caseId} />}

        {activeTab === 'conveyancing' && caseData?.case_type === 'Conveyancing' && (
          <ConveyancingDetailPanel
            authAxios={authAxios}
            showFormMessage={showFormMessage}
            caseId={caseId}
          />
        )}

        {activeTab === 'trust_requisitions' && (
          <TrustRequisitionPanel authAxios={authAxios} showFormMessage={showFormMessage} caseId={caseId} />
        )}

        {activeTab === 'trust_ledger' && (
          <TrustLedgerPanel authAxios={authAxios} showFormMessage={showFormMessage} caseId={caseId} />
        )}
      </div>

      <NewTaskModal
        open={showNewTaskModal}
        onClose={() => setShowNewTaskModal(false)}
        onCreated={fetchRecords}
        authAxios={authAxios}
        showFormMessage={showFormMessage}
        caseId={caseId}
        users={users}
      />
      <LogCommunicationModal
        open={showLogCommModal}
        onClose={() => setShowLogCommModal(false)}
        onCreated={fetchRecords}
        authAxios={authAxios}
        showFormMessage={showFormMessage}
        caseId={caseId}
        clientId={caseData?.client}
      />
      <LogTimeEntryModal
        open={showLogTimeModal}
        onClose={() => setShowLogTimeModal(false)}
        onCreated={fetchRecords}
        authAxios={authAxios}
        showFormMessage={showFormMessage}
        caseId={caseId}
        clientId={caseData?.client}
      />
      <LogDisbursementModal
        open={showLogExpenseModal}
        onClose={() => setShowLogExpenseModal(false)}
        onCreated={fetchRecords}
        authAxios={authAxios}
        showFormMessage={showFormMessage}
        caseId={caseId}
        clientId={caseData?.client}
      />
      <CaseEditModal
        open={showEditCaseModal}
        onClose={() => setShowEditCaseModal(false)}
        onSaved={fetchCaseDetails}
        authAxios={authAxios}
        showFormMessage={showFormMessage}
        caseData={caseData}
      />
    </div>
  );
};

/* ---------- Audit Log Panel (inline) ---------- */
const AuditLogPanel = ({ authAxios, caseId }) => {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [totalCount, setTotalCount] = React.useState(0);
  const pageSize = 50;

  React.useEffect(() => {
    if (!authAxios || !caseId) return;
    setLoading(true);
    authAxios.get(`/cases/${caseId}/audit-log/`, { params: { page, page_size: pageSize } })
      .then(res => {
        const data = res.data;
        // Support both old array format and new paginated format
        if (Array.isArray(data)) {
          setLogs(data);
          setTotalCount(data.length);
        } else {
          setLogs(data.results || []);
          setTotalCount(data.count || 0);
        }
      })
      .catch(() => { setLogs([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [authAxios, caseId, page]);

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) return <div className="text-center py-10 text-gray-500">Loading audit log…</div>;
  if (!logs.length) return <div className="text-center py-10 text-gray-400">No audit log entries yet.</div>;

  const actionIcon = (action) => {
    const map = {
      TASK_CREATED: 'bi-plus-circle text-green-600',
      TASK_STATUS_CHANGED: 'bi-arrow-repeat text-blue-600',
      TASK_UPDATED: 'bi-pencil text-yellow-600',
      TIME_ENTRY_CREATED: 'bi-clock text-purple-600',
      UPLOAD: 'bi-upload text-teal-600',
      UPDATE: 'bi-pencil-square text-orange-600',
      VIEW: 'bi-eye text-gray-500',
      CONFLICT_CHECK: 'bi-shield-check text-red-600',
    };
    return map[action] || 'bi-journal-text text-blue-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Matter Activity Log</h3>
        <span className="text-sm text-gray-500">{totalCount} {totalCount === 1 ? 'entry' : 'entries'}</span>
      </div>
      <div className="space-y-4">
        {logs.map(log => (
          <div key={log.id} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <i className={actionIcon(log.action)}></i>
              </div>
            </div>
            <div className="flex-1 pb-4 border-b border-gray-100">
              <div className="flex justify-between items-start">
                <div>
                  <span className="font-medium">{log.user}</span>
                  <span className="ml-2 px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">{log.action.replace(/_/g, ' ')}</span>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(log.timestamp).toLocaleDateString()} {new Date(log.timestamp).toLocaleTimeString()}
                </div>
              </div>
              {log.description && <div className="text-sm text-gray-700 mt-1">{log.description}</div>}
              {log.details?.changes && (
                <div className="mt-2 text-xs space-y-1">
                  {Object.entries(log.details.changes).map(([field, vals]) => (
                    <div key={field} className="flex gap-2">
                      <span className="font-medium text-gray-500 capitalize">{field.replace(/_/g, ' ')}:</span>
                      <span className="line-through text-red-400">{vals.old || '(empty)'}</span>
                      <span className="text-green-600">→ {vals.new || '(empty)'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Previous</button>
          <span className="px-3 py-1 text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
};

export default CaseDetailsPage;
